/**
 * @file リーダーのサイドパネル「目次」である (本を読む画面の土台がリーダーになったので、
 *   以前ページの右に置いていた案内をここへ移した)。
 *
 * @what: 復習カードを作るボタン、計画の今週の範囲 (計画に入っている本だけ)、いまのページの見出し、目次 (たたんで見出しだけにできる)、
 *   書き込みのあるページ、この面の問題、このページに関する問題、別冊 / 本体へのリンク、
 *   下部に呼び出し側が渡すリンク (読み方の切り替え・さくいん・問題ごとの解説) を縦に並べる。
 * @why: 目次は常に見たいものではない (代表の判断、2026-09-09)。パネルごと閉じられるうえに、
 *   開いていても一覧をたたんで、いまいる章だけを見られるようにする。
 * @failure: 描画のみ。無いものは節ごと出さない。
 *
 * ページ番号は資料の連番で受け取り、画面には紙に刷られた番号を出す (別冊は別冊自身の差で
 * 直す)。行き先の `?page=` は連番のままにする。
 */

import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { StudyScanItem } from "../api/scans";
import type { StudySource, StudySourceTocEntry } from "../api/sources";
import { entrySummary } from "../model/corpusIndex";
import { companionPageFor, printedPageOf } from "../model/sourcePages";
import type { RelatedPageGroup } from "../model/pageLinks";
import type { ReverseGroup } from "../model/sourceLinks";

/** 「このページに関する問題」に並べる上限 (超えた分は件数だけ出す)。 */
const RELATED_ITEMS_MAX = 8;

export function PageTocPanel({
  source,
  currentPage,
  headings,
  onThisPage,
  relatedGroups,
  relatedPages = [],
  companions,
  parent,
  onGoto,
  onOpenItem,
  sourceHref,
  onMakeCard,
  inkPages,
  weekRange,
  footer,
}: {
  source: StudySource;
  currentPage: number;
  /** いまのページを含む見出し (同じページから始まるものは複数)。 */
  headings: readonly StudySourceTocEntry[];
  /** この面に載る問 (切り出し済みのもの)。 */
  onThisPage: readonly StudyScanItem[];
  /** 自分を指す資料の、このページの語を持つ問である。 */
  relatedGroups: readonly ReverseGroup[];
  /** このページに関係する別の資料のページ (ページの対応の両向き)。 */
  relatedPages?: readonly RelatedPageGroup[];
  /** 別冊 (解答例など)。 */
  companions: readonly StudySource[];
  /** 自分が別冊のときの本体である。 */
  parent: StudySource | null;
  onGoto: (page: number) => void;
  onOpenItem: (itemId: string) => void;
  sourceHref: (id: string, query?: string) => string;
  onMakeCard?: ((page: number) => void) | undefined;
  /** このまなぶ人が書き込んだページの番号 (昇順)。無ければ節ごと出さない。 */
  inkPages?: readonly number[];
  /** 計画の今週の範囲 (連番、閉区間)。計画に入っていない本では無し。 */
  weekRange?: { from: number; to: number } | undefined;
  /** 下部に置くリンク (読み方の切り替え・さくいん・問題ごとの解説など)。 */
  footer?: ReactNode;
}) {
  const [tocOpen, setTocOpen] = useState(true);

  return (
    <div className="study-viewer-panel">
      {onMakeCard ? (
        <p className="study-viewer-panel-row">
          <button type="button" className="study-ghost-btn" onClick={() => onMakeCard(currentPage)}>
            復習カードを作る
          </button>
        </p>
      ) : null}

      {weekRange ? (
        <p className="study-week-range">
          <span>今週の範囲</span>
          <button type="button" className="study-link-btn" onClick={() => onGoto(weekRange.from)}>
            p.{printedPageOf(source, weekRange.from)}〜p.{printedPageOf(source, weekRange.to)}
          </button>
          {currentPage >= weekRange.from && currentPage <= weekRange.to ? (
            <span className="study-muted">いまこの範囲を読んでいます</span>
          ) : null}
        </p>
      ) : null}

      {headings.length > 0 ? (
        <div className="study-page-headings">
          {headings.map((h) => (
            <span key={`${h.title}-${h.page}`} className="study-page-heading">
              {h.title}
            </span>
          ))}
        </div>
      ) : null}

      {source.toc && source.toc.length > 0 ? (
        <nav className="study-toc" aria-label="目次">
          <div className="study-reader-review-title study-toc-head">
            <span>目次</span>
            <button
              type="button"
              className="study-link-btn"
              aria-expanded={tocOpen}
              onClick={() => setTocOpen((o) => !o)}
            >
              {tocOpen ? "たたむ" : "ひらく"}
            </button>
          </div>
          {tocOpen ? (
            <ol className="study-toc-list">
              {source.toc.map((e, i) => {
                const isCurrent = headings.some((h) => h.title === e.title && h.page === e.page);
                return (
                  <li key={`${e.page}-${i}`}>
                    <button
                      type="button"
                      className="study-toc-item"
                      aria-current={isCurrent ? "true" : undefined}
                      onClick={() => onGoto(e.page)}
                    >
                      <span className="study-toc-page">p.{printedPageOf(source, e.page)}</span>{" "}
                      <span>{e.title}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          ) : null}
        </nav>
      ) : null}

      {inkPages && inkPages.length > 0 ? (
        <div className="study-reader-links">
          <div className="study-reader-review-title">書き込みのあるページ</div>
          <ul className="study-ink-pages">
            {inkPages.map((p) => (
              <li key={p}>
                <button
                  type="button"
                  className="study-ink-page-btn"
                  aria-current={p === currentPage ? "true" : undefined}
                  aria-label={`書き込みのあるページ p.${printedPageOf(source, p)}`}
                  onClick={() => onGoto(p)}
                >
                  p.{printedPageOf(source, p)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {onThisPage.length > 0 ? (
        <div className="study-reader-links">
          <div className="study-reader-review-title">この面の問題</div>
          <ul className="study-link-list">
            {onThisPage.map((i) => (
              <li key={i.id}>
                <button type="button" className="study-link-btn" onClick={() => onOpenItem(i.id)}>
                  <span className="study-reader-item-id">{i.id}</span> {entrySummary(i)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {relatedPages.length > 0 ? (
        <div className="study-reader-links">
          <div className="study-reader-review-title">関係するページ</div>
          {relatedPages.map((g) => (
            <div key={g.source.id} className="study-related-group">
              <div className="study-related-group-title">{g.source.title}</div>
              <ul className="study-link-list">
                {g.pages.map((p) => (
                  <li key={p.page}>
                    <Link to={sourceHref(g.source.id, `?page=${p.page}`)}>
                      <span className="study-reader-item-id">p.{printedPageOf(g.source, p.page)}</span>
                      {p.label ? ` ${p.label}` : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}

      {relatedGroups.length > 0 ? (
        <div className="study-reader-links">
          <div className="study-reader-review-title">このページに関する問題</div>
          {relatedGroups.map((g) => (
            <div key={g.source.id} className="study-related-group">
              <div className="study-related-group-title">{g.source.title}</div>
              <ul className="study-link-list">
                {g.items.slice(0, RELATED_ITEMS_MAX).map((i) => (
                  <li key={i.id}>
                    <Link to={sourceHref(g.source.id, `?item=${encodeURIComponent(i.id)}`)}>
                      <span className="study-reader-item-id">{i.id}</span> {entrySummary(i)}
                    </Link>
                  </li>
                ))}
              </ul>
              {g.items.length > RELATED_ITEMS_MAX ? (
                <p className="study-muted study-related-more">
                  ほか {g.items.length - RELATED_ITEMS_MAX} 問
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {companions.length > 0 ? (
        <div className="study-reader-links">
          <div className="study-reader-review-title">別冊</div>
          <ul className="study-link-list">
            {companions.map((c) => {
              const hits = headings
                .map((h) => ({ title: h.title, page: companionPageFor(c, h.title) }))
                .filter((x): x is { title: string; page: number } => x.page !== null);
              return hits.length > 0 ? (
                hits.map((hit) => (
                  <li key={`${c.id}-${hit.title}`}>
                    <Link to={sourceHref(c.id, `?page=${hit.page}`)}>
                      {c.title}（p.{printedPageOf(c, hit.page)}）
                    </Link>
                  </li>
                ))
              ) : (
                <li key={c.id}>
                  <Link to={sourceHref(c.id)}>{c.title}</Link>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {parent ? (
        <div className="study-reader-links">
          <div className="study-reader-review-title">本体</div>
          <ul className="study-link-list">
            {headings.length > 0 ? (
              headings.map((h) => (
                <li key={h.title}>
                  <Link to={sourceHref(parent.id, `?school=${encodeURIComponent(h.title)}`)}>
                    {h.title}の問題（{parent.title}）
                  </Link>
                </li>
              ))
            ) : (
              <li>
                <Link to={sourceHref(parent.id)}>{parent.title}</Link>
              </li>
            )}
          </ul>
        </div>
      ) : null}

      {footer ? <div className="study-viewer-panel-footer">{footer}</div> : null}
    </div>
  );
}
